import { defineStore } from "pinia";
import { ref } from 'vue'

import { useUserStore } from "./user";
import { useNotificationStore } from './notification';


import { fetchFromBackend } from "./helpers/backend.mjs";

export const useBusinessPartnersListsStore = defineStore("businessPartnersLists", {
  state: () => {
    return {
      lists: [], // a list of Lists defined by a specific filter (search)
    }
  },
  getters: {
    businessPartnersLists: (state) => {
      const userStore = useUserStore();
      const businessPartnersListsExists = state.lists.find((list) => list.signature === userStore.currentSelection.businessPartnersLists.signature);
      // console.log('businessPartnersListsExists', businessPartnersListsExists)
      return (businessPartnersListsExists) ? businessPartnersListsExists : { summary: '', data: [], size: 0 };
    },
    namedFilter: (state) => (name) => {
      const userStore = useUserStore();
      // console.log('requesting namedFilter', name)
      let filter = {
        idCompany: userStore.currentSelection.campaign.company.id,
        idCampaign: userStore.currentSelection.campaign.id,
        idSalesAgent: (userStore.currentSelection.scope === 'salesAgent') ? userStore.currentSelection?.salesAgent?.id : -1,
        idSalesTeam: (userStore.currentSelection.scope === 'salesTeam') ? userStore.currentSelection.salesTeam.id : -1,
      };
      switch (name) {
        case 'LeadList':
          // Erkenne auch Sugar Aktionen, Leads, Aktionen, SugarAktionen, Sugar-Aktionen (case-insensitive, trim)
          const leadListNames = ['leads', 'aktionen', 'sugar aktionen', 'sugaraktionen', 'sugar-aktionen'];
          const leadLists = userStore.filterOption && userStore.filterOption.businessPartnerLists
            ? userStore.filterOption.businessPartnerLists.filter((list) => {
                const name = (list.name || '').toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
                return leadListNames.some(ln => name.includes(ln.replace(/\s+/g, '').replace(/-/g, '')));
              })
            : [];
          if (leadLists && leadLists.length > 0) {
            filter = Object.assign({}, filter, {
              page: 1,
              pages: 1,
              pageSize: 2500,
              id: -9, // new: not yet viewed
              idBusinessPartnerList: leadLists.map((leadList) => leadList.id).join(','),
              idBusinessPartnerStatus: null,
              businessPartnerSalesPitchStatus: { id: 1, dateStart: null, dateEnd: null }, 
              idBusinessPartnerOrder: -1,
              signature: `${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#LeadList`,
              summary: 'Aktuelle Leads'
            });
          } else {
            // Explicit empty signature & marker so UI can hide or show empty state
            return null;
          }
          break;
        default:
          break;
      }
      // console.log('namedFilter', name, '=>', filter)
      return filter;
    }
  },
  actions: {
    /** start a search with a filter, i.e. { idBusinessPartnerList: "list:12" } for businessPartners either from Dashboard or from businessPartnerList
     * @async
     * @param {Object} filter - The filter object to search for businessPartners
     * @param {Array} searchCriteria - Additional search criteria to filter the businessPartners
    */
    async getByFilter(filter, searchCriteria = []) {
      const endpoint = "businessPartners/v1/getListByFilter";
      const userStore = useUserStore();
      const notificationStore = useNotificationStore();

      // see if there is existing data if it exists and its update property is less then 6 hours old and it matches the desired page
      const storedBusinessPartnersLists = null
      // const storedBusinessPartnersLists = this.lists.find((businessPartnersLists) => businessPartnersLists.signature === filter.signature);
      // console.log('getlistsByFilter: checked signature', filter.signature, 'got stored:', storedBusinessPartnersLists, 'filter is', filter)
      // TODO: manage Lists in memory by purging businessPartners from active lists
      // if (storedBusinessPartnerList && storedBusinessPartnerList?.update > (Date.now() - (6 * 60 * 60 * 1000))) return storedBusinessPartnerList;

      // Sortierrichtung nur in view.sort verwenden, nicht im Filter-Objekt für das Backend
      const sortOrder = filter.order;
      const f = { ...filter };
      if (Object.prototype.hasOwnProperty.call(f, 'order')) {
        delete f.order;
      }

      // Für das Dashboard-Widget "Unb. Sugar Aktionen" (Lead-Listen) nur Listen + deren Counts laden
      // statt vollständiger GP-Details => spezieller Mode
      if (!f.mode) {
        f.mode = "countBusinessPartnerPerList";
      }

      const payload = {
        id: userStore.id,
        filter: f,
        view: {
          page: 1,
          pageSize: filter.pageSize * filter.pages,
          searchCriteria: searchCriteria,
          sort: {
            field: filter.idBusinessPartnerOrder,
            order: (sortOrder || 'desc')
          }
        },
      }
      if (sortOrder) payload.view.sort.order = sortOrder;

      let response = null;
      try {
        response = await fetchFromBackend(endpoint, payload);
        // notificationStore.addNotification(`BusinessPartner-Listen "${filter.summary}" erfolgreich geladen!`, 'success');
      } catch (error) {
        notificationStore.addNotification(`BusinessPartner-Listen konnten nicht geladen werden! (${error?.message || error})`, 'error', 15);
        return false;
      }
      if (!response.businessPartnerList) {
        notificationStore.addNotification('Keine BusinessPartner-Listen gefunden!', 'error', 16);
        return false;
      }
      userStore.currentSelection.businessPartnersLists.signature = filter.signature;
      if (!storedBusinessPartnersLists) {
        this.lists = []; // clear the lists to prevent dublicates
        this.lists.push({ 
          signature: filter.signature, 
          filter: filter, searchCriteria: searchCriteria, 
          sort: { id: filter.idBusinessPartnerOrder, order: (sortOrder || 'desc') }, 
          summary: response.filter.summary,
          // Backend liefert in diesem Mode businessPartnerList = [{id, name, businessPartnerCount}, ...]
          data: response.businessPartnerList,
          size: response.view.countBusinessPartnerFiltered, 
          update: Date.now() 
        });
      } else {
        storedBusinessPartnersLists.update = Date.now();
        storedBusinessPartnersLists.data = response.businessPartnerList;
      }
    },
    /** at campaign change or in certain intervals, the businessPartnerList data needs to be refreshed to reflect up to date 'Wiedervorlagen', 'vor Ort Termine' and 'Sugar Aktionen' 
     * @async
    */
    async refresh() {
      const userStore = useUserStore();
      const notificationStore = useNotificationStore();
      // if (userStore.currentSelection.scope !== 'salesAgent') return false;
      const leadFilter = this.namedFilter('LeadList');
      const taskList = [];
      if (leadFilter) taskList.push(this.getByFilter(leadFilter));
      // console.log(this.namedFilter('LeadList'))
      userStore.currentSelection.businessPartnersLists.isLoading = true;
      try {
        await Promise.all(taskList);
        // notificationStore.addNotification('BusinessPartner-Listen erfolgreich aktualisiert!', 'success');
      } catch (error) {
        notificationStore.addNotification(`BusinessPartner-Listen-Refresh fehlgeschlagen! (${error?.message || error})`, 'error', 17);
      }
      userStore.currentSelection.businessPartnersLists.isLoading = false;
    },
  },
});